import { colors } from 'app-v2/design-systems/theme/colors'
import { COLOR } from 'app-v2/design-tokens'
import { CircleQuestion } from 'app/assets/icons'
import { breakpoints, ButtonMedium, media, style } from 'app/assets/styles'
import { SignupForm } from 'app/common/forms'
import type { MenuListItem } from 'app/common/layout/menu'
import { MenuItem, MenuLayout } from 'app/common/layout/menu'
import { SplitLayout } from 'app/common/layout/split'
import { Link } from 'app/common/link'
import { Loader } from 'app/common/loader'
import { Picture as PictureComponent } from 'app/common/picture'
import { KingdomCard } from 'app/kingdom/card'
import { Points } from 'app/kingdom/points'
import { FormCoupon, Privileges } from 'app/kingdom/privileges'
import { KingdomTutorialModal } from 'app/kingdom/tutorials'
import React, { Component } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import Modal from 'react-modal'
import type {
    Coupon,
    KingdomPoints,
    MarketingAnimationShortDTO
} from 'shared/types'
import { isDefined } from 'shared/utils'
import styled from 'styled-components'
import { BigPlayButton, ControlBar, Player } from 'video-react'

type State = {
    isSocialPending: boolean,
    width: number,
    left: boolean,
    shouldShowTuto: boolean
}

type Props = {
    fetchDashboardData: () => void,
    fetchPointsData: () => void,
    fetchCardData: () => void,
    kingdomItems: Array<MenuListItem>,
    kingdomAuthCode: string,
    logout: () => void,
    logoutDone: (params: { isGlobal: boolean }) => void,
    marketingAnimation?: MarketingAnimationShortDTO,
    coupons: Array<Coupon>,
    couponsLoaded: boolean,
    pointInfos: KingdomPoints,
    authenticated: boolean,
    dataLoaded: boolean,
    showTuto: boolean,
    intl: IntlShape,
    navigationState: any
}

const { xsmallLegacy } = breakpoints

class Dashboard extends Component<Props, State> {
    constructor(props: Props) {
        super()
        const left = props.navigationState && props.navigationState.left
        this.state = {
            width: window.innerWidth,
            isSocialPending: !props.authenticated,
            left: isDefined(left) ? left : true,
            shouldShowTuto: props.showTuto
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { authenticated, fetchDashboardData } = this.props
        if (prevProps.authenticated !== authenticated && authenticated) {
            fetchDashboardData()
            this.setState({ isSocialPending: !authenticated })
        }
    }

    componentDidMount() {
        const {
            fetchDashboardData,
            authenticated,
            fetchPointsData,
            fetchCardData
        } = this.props
        if (authenticated) {
            fetchDashboardData()
            fetchPointsData()
            fetchCardData()
        }
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    logout = async (params: { isGlobal: boolean }) => {
        const { logout, logoutDone } = this.props
        await logout()
        logoutDone(params)
    }

    renderMarketingAnimation = () => {
        const { marketingAnimation } = this.props
        if (!marketingAnimation) {
            return null
        }
        const {
            actionUrl,
            external,
            imageUrlLandscape,
            enableVideo,
            autoPlay,
            videoUrlLandscape,
            audio,
            repeat
        } = marketingAnimation
        return (
            <StyledLink external={external} url={actionUrl}>
                {enableVideo ? (
                    <StyledPlayer
                        fluid
                        playsInline
                        muted={!audio}
                        autoPlay={autoPlay}
                        loop={repeat}
                        key={videoUrlLandscape}>
                        <source src={videoUrlLandscape} />
                        <ControlBar disabled />
                        <BigPlayButton disabled />
                    </StyledPlayer>
                ) : (
                    <StyledPicture>
                        <PictureComponent
                            singleImage
                            imageUrl={imageUrlLandscape}
                            desktopRatio="27/11"
                            mobileRatio="16/11"
                        />
                    </StyledPicture>
                )}
            </StyledLink>
        )
    }

    showTutorial = () => {
        const { shouldShowTuto } = this.state
        this.setState({ shouldShowTuto: !shouldShowTuto })
    }

    render() {
        const {
            intl,
            kingdomItems,
            coupons,
            couponsLoaded,
            kingdomAuthCode
        } = this.props
        const { isSocialPending, width, left, shouldShowTuto } = this.state
        const mobile = width < xsmallLegacy - 1
        const nbCoupons = coupons && coupons.length
        const breadcrumb = [
            {
                link: intl.formatMessage({ id: 'routes.kingdom.dashboard' }),
                title: intl.formatMessage({
                    id: 'kingdom.dashboard.title.breadcrumb.end'
                })
            }
        ]

        return (
            <MenuItem
                breadcrumb={breadcrumb}
                title={intl.formatMessage({ id: 'kingdom.dashboard.title' })}
                icon={
                    <div onClick={this.showTutorial}>
                        <CircleQuestion />
                    </div>
                }>
                <Modal
                    isOpen={isSocialPending}
                    style={
                        mobile
                            ? style.classicModalMobileStyle
                            : style.classicModalStyle
                    }>
                    <SignupForm />
                </Modal>
                <Modal
                    isOpen={shouldShowTuto}
                    ariaHideApp={false}
                    style={
                        mobile
                            ? style.classicModalMobileStyle
                            : style.classicModalStyle
                    }>
                    <KingdomTutorialModal handleClose={this.showTutorial} />
                </Modal>
                <TilesContainer>
                    {!!kingdomAuthCode && <KingdomCard />}
                    {this.renderMarketingAnimation()}
                </TilesContainer>
                <SplitContainer>
                    <Loader loading={!couponsLoaded}>
                        <SplitLayout
                            activeLeft={left}
                            idTitleLeft="kingdom.dashboard.points.title"
                            leftBlock={<Points />}
                            idTitleRight="kingdom.dashboard.privileges.title"
                            valuesTitleRight={{ number: nbCoupons }}
                            rightBlock={<Privileges />}
                            className="dashboard"
                        />
                        <FormCoupon withoutPickUpType />
                    </Loader>
                </SplitContainer>
                <MenuLayout
                    background="transparent"
                    menuItems={kingdomItems.filter(m => m.inMenuMobile)}
                    route="routes.kingdom.dashboard"
                    hideMobileMenu={false}
                    hideDesktopMenu
                    className="dashboard"
                />
                <LogoutButtonContainer>
                    <FormattedMessage id="kingdom.logout.button.local.label">
                        {(text: string) => (
                            <StyledButtonMedium
                                background={colors.fieryRed}
                                onClick={() => this.logout({ isGlobal: false })}
                                color={COLOR.SECONDARY}>
                                {text}
                            </StyledButtonMedium>
                        )}
                    </FormattedMessage>
                    <FormattedMessage id="kingdom.logout.button.global.label">
                        {(text: string) => (
                            <StyledButtonMedium
                                background={colors.fieryRed}
                                onClick={() => this.logout({ isGlobal: true })}
                                color={COLOR.SECONDARY}>
                                {text}
                            </StyledButtonMedium>
                        )}
                    </FormattedMessage>
                </LogoutButtonContainer>
            </MenuItem>
        )
    }
}

const SplitContainer = styled.div`
    margin: -50px 0 20px;
    ${media.medium`
        margin: 0 0 20px;
    `};
`

const LogoutButtonContainer = styled.div`
    padding: 24px;
    margin: 0 auto;
    display: flex;
    gap: 16px;
    flex-direction: column;
    justify-content: center;
    ${media.medium`
        width: 33%;
        flex-direction: row;
    `};
    ${media.large`
        width: 550px;
    `};
`

const StyledLink = styled(Link)`
    box-sizing: border-box;
    overflow: hidden;
    margin: 15px 0;
    img {
        border-radius: 10px;
        width: 100%;
    }
    width: 100%;
    ${media.medium`
        width: calc(50% - 15px);
    `};
    border-radius: 10px;
`

const StyledPicture = styled.div`
    font-size: 0;
`

const TilesContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    ${media.medium`
        width: ${window.innerWidth - 400}px;
    `}
    ${media.large`
        width: auto;
        margin-left: -10%;
    `}
`

const StyledButtonMedium = styled(ButtonMedium)`
    ${style.buttonMedium};
    border: none;
`

const StyledPlayer = styled(Player)`
    width: 100%;
    padding-top: 0 !important;
    font-size: 0;
    border-radius: 12px;
`

const DashboardComponent = injectIntl(Dashboard)

export { DashboardComponent }
